/**
 * Notification Sound Utility
 * 
 * Uses Web Audio API to generate a subtle two-tone alert sound
 * for critical events (delivery failures, rejections).
 * No external audio files needed.
 */

let audioContext: AudioContext | null = null;

/**
 * Initialize AudioContext lazily (needs user interaction first)
 */
const getAudioContext = (): AudioContext | null => {
  if (typeof window === 'undefined') return null;
  
  if (!audioContext) {
    try {
      audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    } catch (e) {
      console.warn('Web Audio API not supported');
      return null;
    }
  }
  
  // Resume if suspended (browser autoplay policy)
  if (audioContext.state === 'suspended') {
    audioContext.resume();
  }
  
  return audioContext;
};

/**
 * Play a subtle two-tone alert sound
 * Sound design: quick ascending two-note chime (not jarring)
 */
export const playAlertSound = (): void => {
  const ctx = getAudioContext();
  if (!ctx) return;
  
  try {
    const now = ctx.currentTime;
    
    // Create oscillator for the first tone
    const osc1 = ctx.createOscillator();
    const gain1 = ctx.createGain();
    
    osc1.type = 'sine';
    osc1.frequency.setValueAtTime(440, now); // A4 note
    
    gain1.gain.setValueAtTime(0, now);
    gain1.gain.linearRampToValueAtTime(0.15, now + 0.02); // Quick attack
    gain1.gain.linearRampToValueAtTime(0, now + 0.15); // Quick decay
    
    osc1.connect(gain1);
    gain1.connect(ctx.destination);
    
    osc1.start(now);
    osc1.stop(now + 0.15);
    
    // Create oscillator for the second tone (slightly higher, slightly delayed)
    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    
    osc2.type = 'sine';
    osc2.frequency.setValueAtTime(554.37, now + 0.08); // C#5 note (ascending interval)
    
    gain2.gain.setValueAtTime(0, now + 0.08);
    gain2.gain.linearRampToValueAtTime(0.12, now + 0.1);
    gain2.gain.linearRampToValueAtTime(0, now + 0.25);
    
    osc2.connect(gain2);
    gain2.connect(ctx.destination);
    
    osc2.start(now + 0.08);
    osc2.stop(now + 0.25);
  } catch (e) {
    console.warn('Failed to play alert sound:', e);
  }
};

/**
 * Play a more urgent alert for critical events
 * Two quick pulses at a higher pitch
 */
export const playCriticalAlertSound = (): void => {
  const ctx = getAudioContext();
  if (!ctx) return;
  
  try {
    const now = ctx.currentTime;
    
    // First pulse
    const osc1 = ctx.createOscillator();
    const gain1 = ctx.createGain();
    
    osc1.type = 'triangle';
    osc1.frequency.setValueAtTime(880, now); // A5 - higher pitch
    
    gain1.gain.setValueAtTime(0, now);
    gain1.gain.linearRampToValueAtTime(0.18, now + 0.015);
    gain1.gain.linearRampToValueAtTime(0, now + 0.1);
    
    osc1.connect(gain1);
    gain1.connect(ctx.destination);
    
    osc1.start(now);
    osc1.stop(now + 0.1);
    
    // Second pulse (quick repeat)
    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    
    osc2.type = 'triangle';
    osc2.frequency.setValueAtTime(880, now + 0.12);
    
    gain2.gain.setValueAtTime(0, now + 0.12);
    gain2.gain.linearRampToValueAtTime(0.18, now + 0.135);
    gain2.gain.linearRampToValueAtTime(0, now + 0.22);
    
    osc2.connect(gain2);
    gain2.connect(ctx.destination);
    
    osc2.start(now + 0.12);
    osc2.stop(now + 0.22);
  } catch (e) {
    console.warn('Failed to play critical alert sound:', e);
  }
};
